import { useState, useEffect, useCallback } from "react";
import { ExternalLink, Trash2, RefreshCw, Clock, CalendarCheck } from "lucide-react";
import { BACKEND_URL } from "../../context/AuthContext";

const AdminSubmissionsTab = ({ authToken, onMessage }) => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');
  const headers = { 'Content-Type': 'application/json', ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}) };

  const fetchSubmissions = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/submissions`, { headers });
      const data = await res.json();
      setSubmissions(Array.isArray(data) ? data : []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [authToken]);

  useEffect(() => { fetchSubmissions(); }, [fetchSubmissions]);

  const updateStatus = async (sub, newStatus) => {
    try {
      const url = sub.id
        ? `${BACKEND_URL}/api/submissions/${sub.id}/status`
        : `${BACKEND_URL}/api/submissions/status`;
      await fetch(url, {
        method: 'PUT', headers,
        body: JSON.stringify({ email: sub.email, track_title: sub.track_title, status: newStatus }),
      });
      onMessage(`Submission ${newStatus} successfully`);
      fetchSubmissions();
    } catch (e) { console.error(e); }
  };

  const renewSubmission = async (sub) => {
    if (!sub.id) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/submissions/${sub.id}/renew`, {
        method: 'POST', headers,
      });
      const data = await res.json();
      if (data.status === 'success') {
        onMessage(data.message);
      } else {
        onMessage(data.message || 'Renew failed');
      }
      fetchSubmissions();
    } catch (e) { console.error(e); }
  };

  const deleteSubmission = async (sub) => {
    if (!sub.id) return;
    if (!window.confirm(`Delete "${sub.track_title}" by ${sub.artist_name}?`)) return;
    try {
      await fetch(`${BACKEND_URL}/api/submissions/${sub.id}`, {
        method: 'DELETE', headers,
      });
      onMessage(`Deleted "${sub.track_title}" by ${sub.artist_name}`);
      fetchSubmissions();
    } catch (e) { console.error(e); }
  };

  const getDaysRemaining = (expiresAt) => {
    if (!expiresAt) return null;
    const now = new Date();
    const exp = new Date(expiresAt);
    const diff = Math.ceil((exp - now) / (1000 * 60 * 60 * 24));
    return diff;
  };

  const filtered = submissions.filter(s => filter === 'all' || s.status === filter);
  const statusCounts = {
    pending: submissions.filter(s => s.status === 'pending').length,
    approved: submissions.filter(s => s.status === 'approved').length,
    expired: submissions.filter(s => s.status === 'expired').length,
    rejected: submissions.filter(s => s.status === 'rejected').length,
  };

  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
      <div className="p-6 border-b border-white/10">
        <h2 className="text-lg font-bold text-white mb-4" data-testid="admin-submissions-heading">Artist Track Submissions</h2>
        <div className="flex flex-wrap gap-2">
          {['all', 'pending', 'approved', 'expired', 'rejected'].map(f => (
            <button key={f} onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition capitalize ${filter === f ? 'bg-purple-500/20 text-purple-400' : 'bg-white/5 text-white/50 hover:text-white/80'}`} data-testid={`submission-filter-${f}`}>
              {f} {f !== 'all' && `(${statusCounts[f] || 0})`}
            </button>
          ))}
        </div>
      </div>
      {loading ? (
        <div className="p-12 text-center text-white/40">Loading submissions...</div>
      ) : filtered.length === 0 ? (
        <div className="p-12 text-center text-white/40">No submissions found.</div>
      ) : (
        <div className="divide-y divide-white/5">
          {filtered.map((sub, i) => {
            const daysLeft = getDaysRemaining(sub.expires_at);
            const isExpiringSoon = daysLeft !== null && daysLeft > 0 && daysLeft <= 7;
            return (
              <div key={sub.id || i} className="p-5 hover:bg-white/[0.02] transition" data-testid={`submission-row-${i}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1 flex-wrap">
                      <h3 className="text-white font-semibold">{sub.track_title}</h3>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                        sub.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                        sub.status === 'approved' ? 'bg-green-500/20 text-green-400' :
                        sub.status === 'expired' ? 'bg-orange-500/20 text-orange-400' :
                        'bg-red-500/20 text-red-400'
                      }`} data-testid={`submission-status-${i}`}>{sub.status}</span>
                      {sub.status === 'approved' && daysLeft !== null && (
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium inline-flex items-center gap-1 ${
                          isExpiringSoon ? 'bg-amber-500/15 text-amber-400 border border-amber-500/20' : 'bg-white/5 text-white/40'
                        }`} data-testid={`submission-expires-${i}`}>
                          <Clock className="w-3 h-3" />
                          {daysLeft} day{daysLeft !== 1 ? 's' : ''} left
                        </span>
                      )}
                    </div>
                    <p className="text-white/60 text-sm">by <span className="text-white/80 font-medium">{sub.artist_name}</span></p>
                    {sub.submitter_type && sub.submitter_type !== 'artist' && (
                      <div className="flex items-center gap-2 mt-1.5" data-testid={`submission-rep-info-${i}`}>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-400 font-medium capitalize">{sub.submitter_type}</span>
                        {sub.company_name && <span className="text-xs text-white/50">{sub.company_name}</span>}
                        {sub.submitter_role && <span className="text-xs text-white/40">({sub.submitter_role})</span>}
                        {sub.authorization_note && (
                          <a href={sub.authorization_note.startsWith('http') ? sub.authorization_note : '#'} target="_blank" rel="noopener noreferrer"
                            className="text-xs text-purple-400 hover:text-purple-300 underline ml-1" title={sub.authorization_note}>Auth/Proof</a>
                        )}
                      </div>
                    )}
                    <div className="flex flex-wrap gap-4 mt-2 text-xs text-white/40">
                      <span>Genre: {sub.genre || 'N/A'}</span>
                      <span>PRO: {sub.pro_affiliation}</span>
                      <span>Email: {sub.email}</span>
                      <span>Plays: <span className="text-green-400">{sub.play_count || 0}</span></span>
                      {sub.featured && <span className="text-yellow-400 font-medium">FEATURED</span>}
                      {sub.created_at && <span>Submitted: {new Date(sub.created_at).toLocaleDateString()}</span>}
                      {sub.approved_at && (
                        <span className="inline-flex items-center gap-1">
                          <CalendarCheck className="w-3 h-3 text-green-400" />
                          Approved: {new Date(sub.approved_at).toLocaleDateString()}
                        </span>
                      )}
                      {sub.expires_at && (
                        <span className="inline-flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          Expires: {new Date(sub.expires_at).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                    {sub.streaming_link && (
                      <a href={sub.streaming_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 mt-2 text-purple-400 hover:text-purple-300 text-sm transition">
                        <ExternalLink className="w-3 h-3" /> Listen / Download
                      </a>
                    )}
                    {sub.message && <p className="text-white/30 text-sm mt-2 italic">"{sub.message}"</p>}
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    {sub.status === 'pending' && (
                      <>
                        <button onClick={() => updateStatus(sub, 'approved')} className="bg-green-500/20 hover:bg-green-500/30 text-green-400 px-4 py-2 rounded-lg text-sm font-medium transition" data-testid={`approve-submission-${i}`}>Approve</button>
                        <button onClick={() => updateStatus(sub, 'rejected')} className="bg-red-500/20 hover:bg-red-500/30 text-red-400 px-4 py-2 rounded-lg text-sm font-medium transition" data-testid={`reject-submission-${i}`}>Reject</button>
                      </>
                    )}
                    {sub.status === 'expired' && (
                      <button onClick={() => renewSubmission(sub)} className="bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-400 px-4 py-2 rounded-lg text-sm font-medium transition inline-flex items-center gap-1.5" data-testid={`renew-submission-${i}`}>
                        <RefreshCw className="w-3.5 h-3.5" /> Renew 30d
                      </button>
                    )}
                    <button onClick={() => deleteSubmission(sub)} className="bg-white/5 hover:bg-red-500/20 text-white/30 hover:text-red-400 p-2 rounded-lg transition" data-testid={`delete-submission-${i}`} title="Delete submission">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminSubmissionsTab;
